import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS, SHADOWS } from '../constants/theme';

const PlaceholderPlayerManager = ({ profile, teams, onRefresh }) => {
  const { isDesktop } = useResponsive();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    jersey_number: '',
    team_id: teams[0]?.id || '',
  });

  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('placeholder_players')
        .select(
          `
          *,
          team:teams(name),
          claimed_by_profile:profiles!placeholder_players_claimed_by_fkey(full_name)
        `
        )
        .eq('organization_id', profile.organization_id)
        .is('claimed_at', null) // Only show unclaimed players
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlayers(data || []);
    } catch (err) {
      console.error('Error loading placeholder players:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const playerData = {
        organization_id: profile.organization_id,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        jersey_number: formData.jersey_number.trim() || null,
        team_id: formData.team_id || null,
        created_by: user.id,
      };

      if (editingPlayer) {
        // Update existing player
        const { error: updateError } = await supabase
          .from('placeholder_players')
          .update(playerData)
          .eq('id', editingPlayer);

        if (updateError) throw updateError;
        setSuccess('Player updated successfully!');
      } else {
        // Create new player
        const { error: insertError } = await supabase
          .from('placeholder_players')
          .insert(playerData);

        if (insertError) throw insertError;
        setSuccess('Player created successfully!');
      }

      setShowCreateForm(false);
      setEditingPlayer(null);
      loadPlayers();

      // Notify parent component to refresh
      if (onRefresh) {
        onRefresh();
      }

      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        jersey_number: '',
        team_id: teams[0]?.id || '',
      });
    } catch (err) {
      console.error('Error saving player:', err);
      setError(err.message);
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player.id);
    setFormData({
      first_name: player.first_name,
      last_name: player.last_name,
      jersey_number: player.jersey_number || '',
      team_id: player.team_id || '',
    });
    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (playerId) => {
    if (!window.confirm('Are you sure you want to delete this placeholder player?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('placeholder_players')
        .delete()
        .eq('id', playerId);

      if (deleteError) throw deleteError;

      setSuccess('Player deleted successfully!');
      loadPlayers();

      // Notify parent component to refresh
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error deleting player:', err);
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingPlayer(null);
    setFormData({
      first_name: '',
      last_name: '',
      jersey_number: '',
      team_id: teams[0]?.id || '',
    });
    setError('');
  };

  // Styles
  const containerStyle = {
    padding: SPACING.lg,
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.lg,
    flexWrap: 'wrap',
    gap: SPACING.md,
  };

  const buttonStyle = {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.gray[400],
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.danger,
  };

  const formStyle = {
    backgroundColor: COLORS.background.card,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.md,
    marginBottom: SPACING.lg,
  };

  const inputGroupStyle = {
    marginBottom: SPACING.md,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: SPACING.sm,
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
    color: COLORS.gray[700],
  };

  const inputStyle = {
    width: '100%',
    padding: SPACING.md,
    fontSize: FONT_SIZE.base,
    border: `1px solid ${COLORS.gray[400]}`,
    borderRadius: BORDER_RADIUS.sm,
    boxSizing: 'border-box',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.white,
    boxShadow: SHADOWS.sm,
  };

  const thStyle = {
    padding: SPACING.md,
    textAlign: 'left',
    borderBottom: `2px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: COLORS.gray[700],
  };

  const tdStyle = {
    padding: SPACING.md,
    borderBottom: `1px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
  };

  const cardStyle = {
    backgroundColor: COLORS.white,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.sm,
    marginBottom: SPACING.md,
  };

  const alertStyle = (type) => ({
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.sm,
    marginBottom: SPACING.lg,
    backgroundColor: type === 'error' ? COLORS.alert.error.bg : COLORS.alert.success.bg,
    color: type === 'error' ? COLORS.alert.error.text : COLORS.alert.success.text,
    border: `1px solid ${type === 'error' ? COLORS.alert.error.border : COLORS.alert.success.border}`,
  });

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: FONT_SIZE.xl }}>Placeholder Players</h2>
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingPlayer(null);
            setError('');
          }}
          style={buttonStyle}
        >
          {showCreateForm ? 'Cancel' : '+ Create Player'}
        </button>
      </div>

      {error && <div style={alertStyle('error')}>{error}</div>}
      {success && <div style={alertStyle('success')}>{success}</div>}

      {showCreateForm && (
        <form onSubmit={handleSubmit} style={formStyle}>
          <h3 style={{ marginTop: 0, fontSize: FONT_SIZE.lg }}>
            {editingPlayer ? 'Edit Player' : 'Create New Player'}
          </h3>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>First Name *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              style={inputStyle}
              required
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Last Name *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              style={inputStyle}
              required
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Jersey Number (Optional)</label>
            <input
              type="text"
              value={formData.jersey_number}
              onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
              style={inputStyle}
              placeholder="12"
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Team (Optional)</label>
            <select
              value={formData.team_id}
              onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
              style={inputStyle}
            >
              <option value="">No Team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: SPACING.md }}>
            <button type="submit" style={buttonStyle}>
              {editingPlayer ? 'Update Player' : 'Create Player'}
            </button>
            <button type="button" onClick={handleCancel} style={secondaryButtonStyle}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Loading players...</p>
      ) : players.length === 0 ? (
        <p style={{ color: COLORS.gray[500] }}>
          No placeholder players yet. Create one to get started!
        </p>
      ) : isDesktop ? (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Jersey #</th>
              <th style={thStyle}>Team</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {players.map((player) => (
              <tr key={player.id}>
                <td style={tdStyle}>
                  {player.first_name} {player.last_name}
                </td>
                <td style={tdStyle}>{player.jersey_number || '-'}</td>
                <td style={tdStyle}>{player.team?.name || 'No Team'}</td>
                <td style={tdStyle}>{new Date(player.created_at).toLocaleDateString()}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: SPACING.sm }}>
                    <button
                      onClick={() => handleEdit(player)}
                      style={{ ...buttonStyle, padding: `${SPACING.sm} ${SPACING.md}` }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(player.id)}
                      style={{
                        ...dangerButtonStyle,
                        padding: `${SPACING.sm} ${SPACING.md}`,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // Mobile card view
        <div>
          {players.map((player) => (
            <div key={player.id} style={cardStyle}>
              <h3 style={{ margin: 0, marginBottom: SPACING.sm, fontSize: FONT_SIZE.lg }}>
                {player.first_name} {player.last_name}
                {player.jersey_number && ` #${player.jersey_number}`}
              </h3>
              <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                <strong>Team:</strong> {player.team?.name || 'No Team'}
              </p>
              <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                <strong>Created:</strong> {new Date(player.created_at).toLocaleDateString()}
              </p>
              <div style={{ display: 'flex', gap: SPACING.sm, marginTop: SPACING.md }}>
                <button onClick={() => handleEdit(player)} style={{ ...buttonStyle, flex: 1 }}>
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(player.id)}
                  style={{ ...dangerButtonStyle, flex: 1 }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlaceholderPlayerManager;
